import { useState, useEffect, useCallback } from "react";
import api from "../lib/adminApi";
import { useToast } from "../components/Toast";
import { DollarSign, TrendingDown, Sparkles, TrendingUp, ChevronDown, ChevronUp, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

const CURRENCY = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const PCT = (v) => `${Number(v || 0).toFixed(1)}%`;

const nowISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function AccountsFinancePage() {
  const toast = useToast();
  const [period, setPeriod] = useState(nowISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/accounts-finance/summary?period=${period}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load summary");
    } finally { setLoading(false); }
  }, [period, toast]);

  useEffect(() => { load(); }, [load]);

  const shiftMonth = (delta) => {
    const [y, m] = period.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const sl = data?.subscription_ledger || {};
  const ho = data?.ho_expenses || {};
  const ai = data?.ai_profitability || {};
  const pnl = data?.pnl || {};
  const margins = data?.margins || {};

  return (
    <div className="space-y-6" data-testid="accounts-finance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B0F1A]">Accounts & Finance</h1>
          <p className="text-sm text-[#5B617A] mt-1">Consolidated HO summary for {period}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftMonth(-1)} className="p-2 rounded-xl hover:bg-[#F1F5F9] text-[#5B617A]" data-testid="af-prev-month"><ChevronLeft size={16} /></button>
          <span className="px-3 py-1.5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-sm font-bold text-[#0B0F1A] tabular-nums" data-testid="af-period">{period}</span>
          <button onClick={() => shiftMonth(1)} className="p-2 rounded-xl hover:bg-[#F1F5F9] text-[#5B617A]" data-testid="af-next-month"><ChevronRight size={16} /></button>
          <button onClick={load} disabled={loading} className="p-2 rounded-xl hover:bg-[#F1F5F9] text-[#5B617A] ml-1" data-testid="af-refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-white/60 rounded-2xl animate-pulse border border-[#E2E8F0]" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard icon={DollarSign} accent="blue" label="Subscription Revenue" value={CURRENCY(sl.net_revenue_inr)} sub={`Gross: ${CURRENCY(sl.gross_inr)} · Commission: ${CURRENCY(sl.gateway_commission_inr)}`} testId="af-kpi-revenue" />
            <KpiCard icon={TrendingDown} accent="red" label="HO Expenses" value={CURRENCY(ho.total_inr)} sub={`${Object.keys(ho.by_category || {}).length} categories`} testId="af-kpi-expenses" />
            <KpiCard icon={Sparkles} accent="purple" label="AI Profitability" value={PCT(ai.margin_pct)} sub={`Margin: ${CURRENCY(ai.margin_inr)}`} testId="af-kpi-ai" />
            <KpiCard icon={TrendingUp} accent={pnl.net_profit_inr >= 0 ? "emerald" : "red"} label="Net P&L" value={CURRENCY(pnl.net_profit_inr)} sub={`Margin: ${PCT(pnl.net_margin_pct)}`} testId="af-kpi-pnl" />
          </div>

          {/* Collapsible Sections */}
          <CollapsibleSection title="Subscription Ledger" testId="af-section-ledger" defaultOpen>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCell label="Gross Revenue" value={CURRENCY(sl.gross_inr)} />
              <StatCell label="Gateway Commission" value={CURRENCY(sl.gateway_commission_inr)} />
              <StatCell label="Net Revenue" value={CURRENCY(sl.net_revenue_inr)} bold />
              <StatCell label="Trial Tenants" value={sl.trial_count ?? 0} />
              <StatCell label="Paid Tenants" value={sl.paid_count ?? 0} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="HO Expenses" testId="af-section-expenses" defaultOpen>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCell label="Total" value={CURRENCY(ho.total_inr)} bold />
              {Object.entries(ho.by_category || {}).map(([cat, val]) => (
                <StatCell key={cat} label={cat} value={CURRENCY(val)} />
              ))}
              {Object.keys(ho.by_category || {}).length === 0 && (
                <p className="col-span-full text-sm text-[#94A3B8] py-3">No expenses recorded this month.</p>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="AI Profitability" testId="af-section-ai">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCell label="AI Costs" value={CURRENCY(ai.ai_costs_inr)} />
              <StatCell label="Net Revenue (AI)" value={CURRENCY(ai.net_revenue_inr)} />
              <StatCell label="Margin (₹)" value={CURRENCY(ai.margin_inr)} bold />
              <StatCell label="Margin (%)" value={PCT(ai.margin_pct)} bold />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="P&L Statement" testId="af-section-pnl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCell label="Total Revenue" value={CURRENCY(pnl.revenue_total_inr)} />
              <StatCell label="Total Costs" value={CURRENCY(pnl.total_costs_inr)} />
              <StatCell label="Net Profit" value={CURRENCY(pnl.net_profit_inr)} bold />
              <StatCell label="Net Margin (%)" value={PCT(pnl.net_margin_pct)} bold />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Margins" testId="af-section-margins">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCell label="Gross Margin (₹)" value={CURRENCY(margins.gross_margin_inr)} />
              <StatCell label="Gross Margin (%)" value={PCT(margins.gross_margin_pct)} />
              <StatCell label="Net Margin (₹)" value={CURRENCY(margins.net_margin_inr)} bold />
              <StatCell label="Net Margin (%)" value={PCT(margins.net_margin_pct)} bold />
            </div>
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, accent, label, value, sub, testId }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    red: "bg-red-50 text-red-500 border-red-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${colors[accent]}`}>
          <Icon size={16} />
        </div>
        <p className="text-xs text-[#5B617A] uppercase font-bold tracking-wider">{label}</p>
      </div>
      <p className="text-xl font-extrabold text-[#0B0F1A] tabular-nums" data-testid={`${testId}-val`}>{value}</p>
      {sub && <p className="text-[11px] text-[#94A3B8] mt-1">{sub}</p>}
    </div>
  );
}

function CollapsibleSection({ title, testId, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#FAFBFD] transition-colors">
        <h2 className="font-bold text-[#0B0F1A] text-sm">{title}</h2>
        {open ? <ChevronUp size={16} className="text-[#94A3B8]" /> : <ChevronDown size={16} className="text-[#94A3B8]" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-[#F1F5F9]">{children}</div>}
    </div>
  );
}

function StatCell({ label, value, bold }) {
  return (
    <div className="bg-[#F8FAFC] rounded-xl p-3 mt-3">
      <p className="text-[10px] text-[#5B617A] uppercase font-bold tracking-wider">{label}</p>
      <p className={`text-base tabular-nums mt-1 ${bold ? "font-extrabold text-[#0B0F1A]" : "font-semibold text-[#334155]"}`}>{value}</p>
    </div>
  );
}
